import type { UserAuth } from '@repo/common';
import type { BaseModel } from '@repo/common/types/Sqlite';
import type {
  TranscriptFull,
  TranscriptRow,
  TranscriptStatus
} from '@repo/common/types/Transcript';
import { getPrivateClient } from '@repo/sharedlib/src/data/common.data';
import { safeToISOString } from '@repo/toolslib/src/utils/date.util';
import { parse, stringify } from '@repo/toolslib/src/utils/serialize.util';
import { ValidationUtils } from '@repo/toolslib/src/utils/validation.util';
import { PrivateClientDatabaseAdapter } from '../private.client.adapter';
import type { DeduplicationStrategy } from '../types';
import type { TranscriptCommonItem } from '@repo/common/types/Adapter';

export class TranscriptClientAdapter extends PrivateClientDatabaseAdapter<
  TranscriptRow,
  TranscriptCommonItem
> {
  constructor(
    sqliteModel: BaseModel<TranscriptFull & Record<string, unknown>, TranscriptRow>,
    userId: string,
    authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
    adapterConfig?: Partial<{
      baseUrl: string;
      authToken: string;
      timeout: number;
      retries: number;
    }>
  ) {
    super(sqliteModel, 'transcript', userId, authToken, adapterConfig);
  }

  protected sqliteRowToItem(row: TranscriptRow): TranscriptCommonItem {
    // Ensure status is a valid TranscriptStatus enum value
    const validStatuses: TranscriptStatus[] = [
      'ready',
      'empty',
      'transcribing',
      'transcribed',
      'error'
    ];
    const safeStatus: TranscriptStatus = validStatuses.includes(row.status as TranscriptStatus)
      ? (row.status as TranscriptStatus)
      : 'transcribed'; // Default to transcribed if invalid

    const result: any = {
      id: row.id,
      name: row.name,
      editedName: row.editedName || undefined,
      summary: row.summary || undefined,
      status: safeStatus,
      eventId: row.eventId,
      fileExists: row.fileExists === true || row.fileExists === 1 || row.fileExists === 'true',
      isDeleted: row.isDeleted === true || row.isDeleted === 1 || row.isDeleted === 'true',
      isFavorite: row.isFavorite === true || row.isFavorite === 1 || row.isFavorite === 'true',
      notionMeetingId: row.notionMeetingId,
      userNotes: row.userNotes || undefined,
      workspaceId: row.workspaceId,
      userId: this.userId, // Use injected userId since SQLite doesn't have it
      date: row.date ? new Date(row.date) : undefined,
      createdAt: new Date(row.createdAt),
      updatedAt: new Date(row.updatedAt),
      deletedAt: row.deletedAt ? new Date(row.deletedAt) : undefined,

      // Parse JSON fields (preserve nulls)
      file: row.file === null ? null : row.file ? parse(row.file) : undefined,
      data: row.data ? parse(row.data) : undefined,
      rawOutput: row.rawOutput ? parse(row.rawOutput) : undefined,
      editedOutput: row.editedOutput ? parse(row.editedOutput) : undefined,
      meeting: row.meeting ? parse(row.meeting) : undefined,
      output: row.output ? parse(row.output) : undefined,
      pushedAt: row.pushedAt ? new Date(row.pushedAt) : undefined
    };

    // Only include duration if it's >= 1 second (sub-second durations are meaningless)
    if (row.duration != null && row.duration >= 1) {
      result.duration = row.duration;
    }

    return result;
  }

  protected itemToSqliteRow(item: TranscriptCommonItem): TranscriptRow {
    return {
      id: item.id,
      name: item.name,
      editedName: item.editedName || '',
      summary: item.summary || undefined,
      status: item.status,
      duration: item.duration ?? null,
      eventId: item.eventId,
      fileExists: item.fileExists ? 'true' : 'false',
      isDeleted: item.isDeleted ? 'true' : 'false',
      isFavorite: item.isFavorite ? 1 : 0,
      notionMeetingId: item.notionMeetingId,
      userNotes: item.userNotes,
      workspaceId: item.workspaceId,
      date: safeToISOString(item.date) || new Date().toISOString(), // Required field, use current date as fallback
      createdAt: safeToISOString(item.createdAt) || new Date().toISOString(), // Required field
      updatedAt: safeToISOString(item.updatedAt) || new Date().toISOString(), // Required field
      deletedAt: item.deletedAt ? safeToISOString(item.deletedAt, new Date(), true) || null : null,

      // Serialize JSON fields
      file: item.file ? stringify(item.file) : '',
      data: item.data ? stringify(item.data) : null,
      rawOutput: item.rawOutput ? stringify(item.rawOutput) : '',
      editedOutput: item.editedOutput ? stringify(item.editedOutput) : '',
      meeting: item.meeting ? stringify(item.meeting) : null,
      output: item.output ? stringify(item.output) : '',
      pushedAt: item.pushedAt ? item.pushedAt.toISOString() : null
    };
  }

  protected async getAllFromSqlite(): Promise<TranscriptRow[]> {
    const rows = await (this.sqliteModel as any).findAllRows({ paranoid: true });
    return rows.map((row: any) => this.normalizeRow(row));
  }

  protected async getFromSqliteById(id: string): Promise<TranscriptRow | null> {
    const transcripts = await (this.sqliteModel as any).findAllRows({
      where: { id },
      limit: 1,
      paranoid: true
    });

    if (transcripts.length === 0) {
      console.log(`[TranscriptAdapter] Transcript ${id} not found in SQLite`);
      return null;
    }

    return this.normalizeRow(transcripts[0]);
  }

  private normalizeRow(row: any): TranscriptRow {
    const toIso = (d: any): string => safeToISOString(d) || new Date().toISOString();
    const asBoolText = (v: any): string =>
      v === true || v === 1 || v === 'true' ? 'true' : 'false';
    const maybeStringify = (v: any): string | undefined =>
      v == null ? undefined : typeof v === 'string' ? v : stringify(v);

    return {
      id: row.id,
      name: row.name,
      editedName: row.editedName || '',
      summary: row.summary || undefined,
      status: row.status,
      duration: row.duration != null && row.duration >= 1 ? row.duration : null,
      eventId: row.eventId,
      fileExists: asBoolText(row.fileExists),
      isDeleted: asBoolText(row.isDeleted),
      isFavorite: row.isFavorite === true || row.isFavorite === 1 || row.isFavorite === 'true',
      notionMeetingId: row.notionMeetingId,
      userNotes: row.userNotes,
      workspaceId: row.workspaceId,
      date: toIso(row.date),
      createdAt: toIso(row.createdAt),
      updatedAt: toIso(row.updatedAt),
      deletedAt: row.deletedAt ? safeToISOString(row.deletedAt, undefined, true) : null,
      file: maybeStringify(row.file) ?? '',
      data: maybeStringify(row.data),
      rawOutput: maybeStringify(row.rawOutput) ?? '',
      editedOutput: maybeStringify(row.editedOutput) ?? '',
      meeting: maybeStringify(row.meeting),
      output: maybeStringify(row.output) ?? '',
      pushedAt: row.pushedAt ? new Date(row.pushedAt).toISOString() : undefined
    } as TranscriptRow;
  }

  protected async upsertListToSqliteImpl(rows: TranscriptRow[]): Promise<void> {
    const toJSONString = (val: unknown): string | null | undefined =>
      val === undefined
        ? undefined
        : val === null
          ? null
          : typeof val === 'string'
            ? val
            : stringify(val);

    const serialized = rows.map((row) => ({
      ...row,
      file: toJSONString((row as any).file) ?? '',
      data: toJSONString((row as any).data),
      rawOutput: toJSONString((row as any).rawOutput) ?? '',
      editedOutput: toJSONString((row as any).editedOutput) ?? '',
      meeting: toJSONString((row as any).meeting),
      output: toJSONString((row as any).output) ?? ''
    }));

    await (this.sqliteModel as any).bulkUpsertItemsRaw(serialized);
  }

  protected getDeduplicationStrategy(): DeduplicationStrategy<TranscriptCommonItem> {
    return {
      type: 'none' as const,
      getKey: (item: TranscriptCommonItem) => item.id
    };
  }

  /**
   * Override getCommonItems to include transcript blob content
   * This ensures that JSON fields (rawOutput, editedOutput, output, meeting) are restored from GCS blobs
   */
  protected async getCommonItems(minimal: boolean = false): Promise<TranscriptCommonItem[]> {
    try {
      // Debug logging for missing auth tokens during API calls
      if (!this.authToken || !this.authToken.idToken || !this.authToken.refreshToken) {
        console.error(`[${this.tableName}] Missing auth tokens during getCommonItems call:`, {
          hasAuthToken: !!this.authToken,
          hasIdToken: !!this.authToken?.idToken,
          hasRefreshToken: !!this.authToken?.refreshToken,
          userId: this.userId,
          tableName: this.tableName
        });
      }

      const authTokenWithFallback = this.getAuthTokenWithFallback();

      // For transcripts, we need to include blob content to restore JSON fields
      // Only include transcript content if not requesting minimal data
      const queryParams = minimal ? '?minimal=true' : '?includeTranscript=true';

      const client = getPrivateClient(authTokenWithFallback, this.adapterConfig);
      const response = await client.get(`/adapter/${this.tableName}${queryParams}`);

      const items = response.data?.items || [];
      // Parse response data to handle dates and other serialized values
      return parse(JSON.stringify(items));
    } catch (error) {
      console.error('Error getting common items from server:', error);
      throw new Error('Failed to get common items from server');
    }
  }

  /**
   * Override validateItemEquality to use the improved validateTranscriptEquality from ValidationUtils
   * This provides more accurate comparison for transcript-specific fields and data structures
   */
  protected validateItemEquality(
    item1: TranscriptCommonItem,
    item2: TranscriptCommonItem
  ): { isEqual: boolean; differences: string[] } {
    // Cast to Transcript type since TranscriptCommonItem is compatible with Transcript
    // The only difference is that file is optional in TranscriptCommonItem but required in Transcript
    return ValidationUtils.validateTranscriptEquality(
      item1 as unknown as TranscriptFull,
      item2 as unknown as TranscriptFull
    );
  }
}
